import { useReadOnly } from 'utils/lifecycle'
import React from 'react'
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import ImageCropDialog from 'components/image/ImageCropDialog'
import useImageUpload from 'components/image/useImageUpload'

const ProfileImageUpload = ({ email, firstName, imageUrl, lastName, onUploaded }) => {
  const readOnly = useReadOnly()
  const fullName = [firstName, lastName].filter(Boolean).join(' ')
  const avatarLabel = fullName || email || 'User'
  const initials = [firstName, lastName]
    .filter(Boolean)
    .map((name) => name.charAt(0))
    .join('') || avatarLabel.charAt(0)
  const {
    closeCropDialog,
    cropDialogOpen,
    handleCropComplete,
    handleImageSelect,
    imageToCrop,
    inputRef,
    isUploading,
    openFileDialog,
    previewUrl,
    uploadStatus,
  } = useImageUpload({ uploadUrl: '/api/user/profile-image', onSuccess: onUploaded })

  return (
    <>
      <Stack
        alignItems="center"
        direction="row"
        spacing={2}
        mb={3}
        pb={3}
        borderBottom="1px solid"
        borderColor="divider"
      >
        <Avatar
          alt={avatarLabel}
          src={previewUrl || imageUrl || undefined}
          sx={{ bgcolor: 'primary.main', height: 72, width: 72, fontSize: 24 }}
        >
          {initials.toUpperCase()}
        </Avatar>
        <Box>
          <Typography fontWeight={600} mb={0.75}>Profile photo</Typography>
          <Button
            disabled={readOnly || isUploading}
            onClick={openFileDialog}
            size="small"
            type="button"
            variant="outlined"
          >
            {isUploading && <CircularProgress size={16} sx={{ mr: 1 }} />}
            {imageUrl ? 'Change photo' : 'Upload photo'}
          </Button>
          <Typography color="text.secondary" display="block" mt={0.75} variant="caption">
            JPG or PNG, up to 2 MB. Crop and optimize it before uploading.
          </Typography>
          {uploadStatus && (
            <Typography color={uploadStatus.color} display="block" mt={0.5} variant="caption">
              {uploadStatus.message}
            </Typography>
          )}
        </Box>
      </Stack>
      <input disabled={readOnly}
        accept=".png,.jpg,.jpeg"
        aria-label="Choose profile photo file"
        onChange={handleImageSelect}
        ref={inputRef}
        type="file"
        style={{ display: 'none' }}
      />
      <ImageCropDialog
        allowRectangle={false}
        imageSrc={imageToCrop}
        onClose={closeCropDialog}
        onCropComplete={handleCropComplete}
        open={cropDialogOpen && !readOnly}
      />
    </>
  )
}

export default ProfileImageUpload
